import {
  type BadgeColor,
  GridCellText,
  StatusBadge,
  type StatusBadgeTooltip,
  type TableColumn,
} from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { requestTypeLabelMap } from '~/common/dataRequestUtils';
import { rowBadgeLabel } from '~/common/rowBadgeLabel';
import { type DataRequestV2, RequestType } from '~/common/types';
import { renderRequestStatus } from '~/pages/projectDetails/utilitiesV2/Table/columns';
import AssetType from '../common/AssetType';

// Row-aware name and per-request test id for a status badge.
const badgeProps = (column: string, testIdKey: string, r: DataRequestV2, status: string, cardTitle?: ReactNode) => ({
  'aria-label': rowBadgeLabel(column, status, `${r.agency.agencyName} ${r.agency.type}`, cardTitle),
  'data-testid': `data-request-${testIdKey}-badge-${r.id}`,
});

export const dataRequestColumns: TableColumn<DataRequestV2>[] = [
  {
    id: 'requestId',
    header: 'Request ID',
    size: 120,
    accessorKey: 'requestId',
    cell: ({ row }) => {
      const { id, requestId } = row.original;
      const link = `${window.location.origin}/admin/civil-search/requests/${id}/general`;
      return (
        <GridCellText>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-dark underline flex flex-row items-center"
          >
            <span>{requestId}</span>
          </a>
        </GridCellText>
      );
    },
  },
  {
    id: 'agency.agencyName',
    header: 'Agency',
    size: 200,
    accessorKey: 'agency.agencyName',
    cell: (cell) => {
      const id = cell.row.original.agencyId;
      const value = cell.getValue<string>();
      const link = `${window.location.origin}/admin/civil-search/agencies/${id}`;

      return (
        <GridCellText>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-dark underline flex flex-row items-center"
          >
            <span>{value}</span>
          </a>
        </GridCellText>
      );
    },
  },
  {
    id: 'agency.type',
    header: 'Asset',
    size: 90,
    accessorKey: 'agency.type',
    cell: ({ getValue }) => (
      <GridCellText>
        <AssetType type={getValue<string>()} />
      </GridCellText>
    ),
    meta: {
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      filterValueConfig: {
        type: 'select',
        options: ['Storm', 'Sewer', 'Water', 'Electric', 'Gas'].map((value) => ({
          value,
          label: value,
        })),
      },
    },
  },
  {
    id: 'type',
    header: 'Request Type',
    size: 110,
    accessorFn: (row) => requestTypeLabelMap[row.type] ?? row.type,
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    meta: {
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      filterValueConfig: {
        type: 'select',
        options: [
          { value: RequestType.MapsRequested, label: 'Maps' },
          { value: RequestType.AsbuiltsRequested, label: 'As-Builts' },
        ],
      },
    },
  },
  {
    id: 'liveMap',
    header: 'Live Map',
    size: 120,
    cell: ({ row }) => {
      const agency = row.original.agency;
      let trigger: string;
      let color: BadgeColor;
      let tooltip: StatusBadgeTooltip;

      if (agency.isDigitized) {
        trigger = 'Yes - Digitized';
        color = 'green';
        tooltip = {
          title: 'Digitized Assets',
          description: 'Assets have been digitized for this project by CivilGrid.',
        };
      } else if (agency.isPublished && !agency.assetsInProjectArea) {
        trigger = 'No Assets Present';
        color = 'green';
        tooltip = {
          title: 'No Assets Present',
          description:
            'Based on the digital data in our platform, there are no assets present in the project boundary.',
        };
      } else if (agency.availableDigitally) {
        trigger = 'Yes';
        color = 'green';
        tooltip = {
          title: 'Assets Present',
          description: 'Digital data with layer attributes is available on the map.',
        };
      } else {
        trigger = 'No';
        color = 'grey';
        tooltip = { title: 'Data Not Available', description: 'Digital data NOT available on the map currently.' };
      }

      return (
        <GridCellText>
          <StatusBadge
            {...badgeProps('Live Map', 'live-map', row.original, trigger, tooltip.title)}
            color={color}
            tooltip={tooltip}
          >
            <span className="truncate">{trigger}</span>
          </StatusBadge>
        </GridCellText>
      );
    },
  },
  {
    id: 'download',
    header: 'Download',
    size: 130,
    cell: ({ row }) => {
      const agency = row.original.agency;
      let trigger: string;
      let color: BadgeColor;
      let tooltip: StatusBadgeTooltip;

      if (agency.isDigitized) {
        trigger = 'Yes - Digitized';
        color = 'green';
        tooltip = {
          title: 'Digitized Data Available for Download',
          description: 'Digitized assets for this project are available for download.',
        };
      } else if (agency.isPublished && !agency.assetsInProjectArea) {
        trigger = 'N/A';
        color = 'green';
        tooltip = {
          title: 'Not Applicable',
          description:
            'Based on the digital data in our platform, there are no assets present in the project boundary.',
        };
      } else if (agency.isPublished && !isEmpty(agency.applicableRestrictions)) {
        trigger = 'Approval Required';
        color = 'yellow';
        tooltip = {
          title: 'Approval Required',
          description: `${agency.type} data requires approval before download due to project area size restrictions.`,
        };
      } else if (agency.isPublished) {
        if (agency.applicableRestrictions && !isEmpty(agency.applicableRestrictions)) {
          trigger = 'Approval Required';
          color = 'yellow';
          tooltip = {
            title: 'Approval Required For Download',
            description: `${agency.type} data requires approval prior to download.`,
          };
        } else {
          trigger = 'Yes';
          color = 'green';
          tooltip = { title: 'Available For Download', description: `${agency.type} data is available for download.` };
        }
      } else {
        trigger = '-';
        color = 'grey';
        tooltip = {
          title: 'Digital Data Not Available',
          description: `${agency.type} data is NOT available for download at this time.`,
        };
      }

      return (
        <GridCellText>
          <StatusBadge
            {...badgeProps('Download', 'download', row.original, trigger, tooltip.title)}
            color={color}
            tooltip={tooltip}
          >
            <span className="truncate">{trigger}</span>
          </StatusBadge>
        </GridCellText>
      );
    },
  },
  {
    id: 'map',
    header: 'Map',
    size: 150,
    cell: ({ row }) => {
      const r = row.original;
      const req = r.mapsRequest;

      if (req) {
        const { color, tooltip, trigger } = renderRequestStatus(req.requestStatus, req.utilityPresence, req.outcome);
        return (
          <GridCellText>
            <StatusBadge {...badgeProps('Map', 'map', r, trigger, tooltip.title)} color={color} tooltip={tooltip}>
              <span className="truncate">{trigger}</span>
            </StatusBadge>
          </GridCellText>
        );
      }

      if (r.agency.isPublished) {
        return (
          <GridCellText>
            <StatusBadge
              {...badgeProps('Map', 'map', r, 'N/A')}
              color="green"
              tooltip={{ title: 'Not Applicable', description: 'Digital data is available — no maps request needed.' }}
            >
              <span className="truncate">N/A</span>
            </StatusBadge>
          </GridCellText>
        );
      }
      return (
        <GridCellText>
          <StatusBadge
            {...badgeProps('Map', 'map', r, '-', 'No Request')}
            color="grey"
            tooltip={{ title: 'No Request', description: 'No maps request has been submitted for this agency.' }}
          >
            <span className="truncate">-</span>
          </StatusBadge>
        </GridCellText>
      );
    },
  },
  {
    id: 'asBuilts',
    header: 'As-Builts',
    size: 150,
    cell: ({ row }) => {
      const r = row.original;
      const req = r.asbuiltsRequest;

      if (req) {
        const { color, tooltip, trigger } = renderRequestStatus(req.requestStatus, req.utilityPresence, req.outcome);
        return (
          <GridCellText>
            <StatusBadge
              {...badgeProps('As-Builts', 'asbuilts', r, trigger, tooltip.title)}
              color={color}
              tooltip={tooltip}
            >
              <span className="truncate">{trigger}</span>
            </StatusBadge>
          </GridCellText>
        );
      }

      if (r.agency.asbuiltsAvailable) {
        return (
          <GridCellText>
            <StatusBadge
              {...badgeProps('As-Builts', 'asbuilts', r, 'Request')}
              color="yellow"
              tooltip={{ title: 'As-Builts Available', description: 'As-builts are available for request.' }}
            >
              <span className="truncate">Request</span>
            </StatusBadge>
          </GridCellText>
        );
      }
      return (
        <GridCellText>
          <StatusBadge
            {...badgeProps('As-Builts', 'asbuilts', r, '-', 'No Request')}
            color="grey"
            tooltip={{ title: 'No Request', description: 'No as-builts request has been submitted for this agency.' }}
          >
            <span className="truncate">-</span>
          </StatusBadge>
        </GridCellText>
      );
    },
  },
  {
    id: 'notes',
    header: 'Notes',
    size: 200,
    accessorKey: 'notes',
    cell: ({ getValue }) => <GridCellText>{getValue<string>() || '-'}</GridCellText>,
  },
  {
    id: 'files',
    header: 'Files',
    size: 80,
    accessorFn: (row) => row.files?.length ?? 0,
    cell: ({ getValue }) => <GridCellText>{getValue<number>()}</GridCellText>,
  },
  {
    id: 'createdAt',
    header: 'Created',
    size: 120,
    accessorKey: 'createdAt',
    cell: ({ getValue }) => {
      const dt = getValue<DateTime>();
      return <GridCellText>{dt?.toLocaleString?.(DateTime.DATE_MED) ?? '-'}</GridCellText>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    size: 120,
    accessorKey: 'updatedAt',
    cell: ({ getValue }) => {
      const dt = getValue<DateTime>();
      return <GridCellText>{dt?.toLocaleString?.(DateTime.DATE_MED) ?? '-'}</GridCellText>;
    },
  },
];
